import {
  getUserPreference,
  setUserPreference
} from '../user/user_preferences.js?v=20260812a';

const PLAN_LABEL_SIZE_PREFERENCE_KEY = 'planLabelSize';
const DEFAULT_PLAN_LABEL_SIZE = 'medium';
const DEFAULT_PLAN_LABEL_SCREEN_FONT_SIZE_PX = 11;
const PLAN_LABEL_SCALE_MODES = Object.freeze({
  MODEL: 'model',
  SCREEN: 'screen'
});

const PLAN_LABEL_SIZE_PRESETS = Object.freeze([
  Object.freeze({
    id: 'small',
    labelKey: 'sidebar_rechts.eigenschaften.button.klein',
    fallbackLabel: 'Klein',
    fontSizeMm: 30
  }),
  Object.freeze({
    id: 'medium',
    labelKey: 'sidebar_rechts.eigenschaften.button.mittel',
    fallbackLabel: 'Mittel',
    fontSizeMm: 45
  }),
  Object.freeze({
    id: 'large',
    labelKey: 'sidebar_rechts.eigenschaften.button.gross',
    fallbackLabel: 'Gross',
    fontSizeMm: 60
  })
]);

function normalizePlanLabelSize(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  return PLAN_LABEL_SIZE_PRESETS.some(preset => preset.id === normalized)
    ? normalized
    : DEFAULT_PLAN_LABEL_SIZE;
}

function planLabelSizePreset(value = DEFAULT_PLAN_LABEL_SIZE) {
  const normalized = normalizePlanLabelSize(value);
  return PLAN_LABEL_SIZE_PRESETS.find(preset => preset.id === normalized)
    || PLAN_LABEL_SIZE_PRESETS[1];
}

function currentPlanLabelSize() {
  return normalizePlanLabelSize(getUserPreference(
    PLAN_LABEL_SIZE_PREFERENCE_KEY,
    DEFAULT_PLAN_LABEL_SIZE
  ));
}

function setCurrentPlanLabelSize(value) {
  const normalized = normalizePlanLabelSize(value);
  setUserPreference(PLAN_LABEL_SIZE_PREFERENCE_KEY, normalized);
  return normalized;
}

function planLabelWorldFontSizeMm(value = DEFAULT_PLAN_LABEL_SIZE, multiplier = 1) {
  const safeMultiplier = Number(multiplier);
  return planLabelSizePreset(value).fontSizeMm * (
    Number.isFinite(safeMultiplier) && safeMultiplier > 0 ? safeMultiplier : 1
  );
}

function planLabelScreenFontSize(value, cameraScale, multiplier = 1) {
  const numericScale = Number(cameraScale);
  const safeScale = Number.isFinite(numericScale) && numericScale > 0 ? numericScale : 1;
  return planLabelWorldFontSizeMm(value, multiplier) * safeScale;
}

function resolvePlanLabelMetrics(options = {}) {
  const numericScale = Number(options.cameraScale);
  const cameraScale = Number.isFinite(numericScale) && numericScale > 0 ? numericScale : 1;
  const numericMultiplier = Number(options.multiplier);
  const multiplier = Number.isFinite(numericMultiplier) && numericMultiplier > 0
    ? numericMultiplier
    : 1;
  const scaleMode = options.scaleMode === PLAN_LABEL_SCALE_MODES.SCREEN
    ? PLAN_LABEL_SCALE_MODES.SCREEN
    : PLAN_LABEL_SCALE_MODES.MODEL;
  if (scaleMode === PLAN_LABEL_SCALE_MODES.SCREEN) {
    const numericScreenFontSize = Number(options.screenFontSizePx);
    const screenFontSizePx = Number.isFinite(numericScreenFontSize) && numericScreenFontSize > 0
      ? numericScreenFontSize
      : DEFAULT_PLAN_LABEL_SCREEN_FONT_SIZE_PX;
    const fontSizePx = screenFontSizePx * multiplier;
    return {
      size: 'fixed',
      preset: null,
      scaleMode,
      cameraScale,
      multiplier,
      fontSizeMm: fontSizePx / cameraScale,
      fontSizePx
    };
  }
  const size = normalizePlanLabelSize(options.size ?? currentPlanLabelSize());
  const preset = planLabelSizePreset(size);
  const fontSizeMm = preset.fontSizeMm * multiplier;
  return {
    size,
    preset,
    scaleMode,
    cameraScale,
    multiplier,
    fontSizeMm,
    fontSizePx: fontSizeMm * cameraScale
  };
}

export {
  DEFAULT_PLAN_LABEL_SCREEN_FONT_SIZE_PX,
  DEFAULT_PLAN_LABEL_SIZE,
  PLAN_LABEL_SCALE_MODES,
  PLAN_LABEL_SIZE_PREFERENCE_KEY,
  PLAN_LABEL_SIZE_PRESETS,
  currentPlanLabelSize,
  normalizePlanLabelSize,
  planLabelScreenFontSize,
  planLabelSizePreset,
  planLabelWorldFontSizeMm,
  resolvePlanLabelMetrics,
  setCurrentPlanLabelSize
};
